(() => {
  const register = (name, initializer) => {
    if (window.SafeWalkV51Product?.register) window.SafeWalkV51Product.register(name, initializer);
    else (window.SafeWalkV51ProductQueue ||= []).push([name, initializer]);
  };

  register('gallery', ({ root, listen }) => {
    const gallery = root.querySelector('[data-gallery-enabled], [data-swv51-gallery], .swv5-gallery');
    if (!gallery || gallery.dataset.galleryEnabled === 'false') return;
    const stage = gallery.querySelector('[data-gallery-stage]');
    const thumbsViewport = gallery.querySelector('[data-gallery-thumbs]');
    const previousButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const thumbPreviousButton = gallery.querySelector('[data-gallery-thumb-prev]');
    const thumbNextButton = gallery.querySelector('[data-gallery-thumb-next]');
    const galleryMode = root.dataset.galleryMode || 'harness_assets';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileTrack = window.matchMedia('(max-width: 989px)');
    const variantsNode = root.querySelector('[data-swv5-variants]');
    if (stage && (previousButton || nextButton)) {
      let viewport = stage.closest('.swv5-gallery__viewport');
      if (!viewport) {
        viewport = document.createElement('div');
        viewport.className = 'swv5-gallery__viewport';
        stage.before(viewport);
        viewport.append(stage);
      }
      if (previousButton?.parentElement !== viewport) viewport.append(previousButton);
      if (nextButton?.parentElement !== viewport) viewport.append(nextButton);
    }
    let variants = [];
    try {
      const parsed = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
      variants = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      variants = [];
    }
    const variantMediaIds = new Set(
      variants
        .map((variant) => variant.featured_media?.id)
        .filter(Boolean)
        .map(String)
    );
    const nativeMediaIds = new Set(
      Array.from(gallery.querySelectorAll('[data-media-id]'))
        .map((frame) => String(frame.dataset.mediaId || ''))
        .filter(Boolean)
    );
    const virtualMedia = new Map();
    let activeMediaId = gallery.querySelector('[data-media-id].is-active')?.dataset.mediaId
      || gallery.querySelector('[data-media-id]')?.dataset.mediaId
      || '';
    const initialMediaId = String(activeMediaId || '');
    let galleryInteracted = false, initialAlignmentTimer = 0, initialAlignmentFrame = 0;
    let lastNativeMediaId = nativeMediaIds.has(String(activeMediaId)) ? String(activeMediaId) : '';
    let trackScrollTimer = 0;
    let trackScrollFrame = 0;
    let trackScrollSource = 'swipe';
    let neighborHydrationTimer = 0;
    let neighborHydrationIdle = 0;
    let neighborHydrationGeneration = 0;
    const nativeFrames = ({ visibleOnly = false } = {}) => Array.from(gallery.querySelectorAll('[data-media-id]')).filter((frame) => {
      if (!nativeMediaIds.has(String(frame.dataset.mediaId || ''))) return false;
      return !visibleOnly || !frame.hidden;
    });
    const visibleThumbs = ({ nativeOnly = false } = {}) => Array.from(gallery.querySelectorAll('[data-thumb-id]')).filter((thumb) => {
      if (thumb.hidden || getComputedStyle(thumb).display === 'none') return false;
      return !nativeOnly || nativeMediaIds.has(String(thumb.dataset.thumbId || ''));
    });
    const hydrateFrame = (frame) => {
      if (!frame) return;
      const image = frame.querySelector('img[data-src]');
      if (image) {
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }
      const videoSource = frame.querySelector('video source[data-src]');
      const video = frame.querySelector('video[data-poster]');
      if (video?.dataset.poster && !video.getAttribute('poster')) {
        video.setAttribute('poster', video.dataset.poster);
        video.removeAttribute('data-poster');
      }
      if (videoSource) {
        videoSource.src = videoSource.dataset.src;
        videoSource.removeAttribute('data-src');
        videoSource.closest('video')?.load();
      }
    };
    const hydrateNeighbors = (frames, activeIndex) => {
      [activeIndex, activeIndex - 1, activeIndex + 1].forEach((index) => {
        if (index < 0 || index >= frames.length) return;
        const frame = frames[index];
        if (index !== activeIndex && frame.dataset.mediaType === 'video') return;
        hydrateFrame(frame);
      });
    };
    const cancelDeferredNeighborHydration = () => {
      neighborHydrationGeneration += 1;
      window.clearTimeout(neighborHydrationTimer);
      neighborHydrationTimer = 0;
      if (neighborHydrationIdle && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(neighborHydrationIdle);
      }
      neighborHydrationIdle = 0;
    };
    const deferNeighborHydration = (frames, activeIndex) => {
      cancelDeferredNeighborHydration();
      const generation = neighborHydrationGeneration;
      const hydrate = () => {
        if (generation !== neighborHydrationGeneration) return;
        neighborHydrationIdle = 0;
        hydrateNeighbors(frames, activeIndex);
      };
      const queueIdle = () => {
        if (generation !== neighborHydrationGeneration) return;
        neighborHydrationTimer = window.setTimeout(() => {
          if (generation !== neighborHydrationGeneration) return;
          neighborHydrationTimer = 0;
          if ('requestIdleCallback' in window) {
            neighborHydrationIdle = window.requestIdleCallback(hydrate, { timeout: 1500 });
          } else {
            hydrate();
          }
        }, 250);
      };
      if (document.readyState === 'complete') queueIdle();
      else listen(window, 'load', queueIdle, { once: true });
    };
    const scrollStageToFrame = (frame, source) => {
      if (!stage || !mobileTrack.matches || !frame || frame.hidden) return;
      const stageRect = stage.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();
      const rtl = getComputedStyle(stage).direction === 'rtl';
      const alignmentDelta = rtl ? frameRect.right - stageRect.right : frameRect.left - stageRect.left;
      if (Math.abs(alignmentDelta) < 2) return;
      trackScrollSource = source;
      const useImmediateScroll = reducedMotion.matches
        || source === 'initial'
        || source === 'resize'
        || frame.dataset.mediaType === 'virtual';
      stage.scrollBy({
        left: alignmentDelta,
        behavior: useImmediateScroll ? 'auto' : 'smooth'
      });
    };
    const setMedia = (mediaId, centerThumb = true, source = 'api', syncStage = true) => {
      if (!mediaId) return false;
      const id = String(mediaId);
      const frames = Array.from(gallery.querySelectorAll('[data-media-id]'));
      const activeIndex = frames.findIndex((frame) => frame.dataset.mediaId === id);
      if (activeIndex < 0 || frames[activeIndex].hidden) return false;
      if (source === 'initial') {
        hydrateFrame(frames[activeIndex]);
        deferNeighborHydration(frames, activeIndex);
      } else {
        cancelDeferredNeighborHydration();
        hydrateNeighbors(frames, activeIndex);
      }
      const changed = activeMediaId !== id;
      activeMediaId = id;
      if (nativeMediaIds.has(id)) lastNativeMediaId = id;
      frames.forEach((frame) => {
        const active = frame.dataset.mediaId === id;
        frame.classList.toggle('is-active', active);
        frame.setAttribute('aria-hidden', String(!active));
        frame.toggleAttribute('inert', !active);
        const video = frame.querySelector('video');
        if (!video) return;
        if (active && !reducedMotion.matches) video.play().catch(() => {});
        else video.pause();
      });
      gallery.querySelectorAll('[data-thumb-id]').forEach((thumb) => {
        const active = thumb.dataset.thumbId === id;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const activeThumb = Array.from(gallery.querySelectorAll('[data-thumb-id]')).find((thumb) => thumb.dataset.thumbId === id);
      if (centerThumb && activeThumb && thumbsViewport) {
        const viewportRect = thumbsViewport.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        const offset = thumbRect.left + thumbRect.width / 2 - viewportRect.left - viewportRect.width / 2;
        thumbsViewport.scrollBy({ left: offset, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      if (syncStage) scrollStageToFrame(frames[activeIndex], source);
      if (changed) {
        root.dispatchEvent(new CustomEvent('swv5:gallery-change', {
          bubbles: true,
          detail: { mediaId: id, virtual: virtualMedia.has(id), source }
        }));
      }
      return true;
    };
    const restoreNative = (centerThumb = false) => {
      const visibleNativeThumb = visibleThumbs().find((thumb) => nativeMediaIds.has(String(thumb.dataset.thumbId || '')));
      const availableLastNative = nativeFrames({ visibleOnly: true })
        .find((frame) => frame.dataset.mediaId === lastNativeMediaId);
      const firstNativeFrame = nativeFrames({ visibleOnly: true })[0];
      return setMedia(
        availableLastNative?.dataset.mediaId || visibleNativeThumb?.dataset.thumbId || firstNativeFrame?.dataset.mediaId,
        centerThumb,
        'restore'
      );
    };
    const unregisterVirtualMedia = (mediaId) => {
      const id = String(mediaId || '');
      const entry = virtualMedia.get(id);
      if (!entry) return false;
      if (activeMediaId === id) restoreNative(false);
      entry.thumb?.removeEventListener('click', entry.onThumbClick);
      entry.frame?.remove();
      entry.thumb?.remove();
      virtualMedia.delete(id);
      return true;
    };
    const registerVirtualMedia = ({
      id,
      frame,
      thumb,
      preserveOnVariantChange = false
    } = {}) => {
      const mediaId = String(id || '');
      if (!mediaId || nativeMediaIds.has(mediaId) || virtualMedia.has(mediaId) || !frame) return false;
      frame.dataset.mediaId = mediaId;
      frame.dataset.mediaType = 'virtual';
      frame.classList.add('swv5-gallery__frame');
      frame.classList.remove('is-active');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('inert', '');
      stage?.insertBefore(frame, stage.querySelector('.swv5-gallery__nav'));
      let onThumbClick = null;
      if (thumb && thumbsViewport) {
        thumb.dataset.thumbId = mediaId;
        thumb.classList.add('swv5-gallery__thumb');
        thumb.setAttribute('aria-pressed', 'false');
        onThumbClick = () => { galleryInteracted = true; setMedia(mediaId, true, 'thumb'); };
        thumb.addEventListener('click', onThumbClick);
        thumbsViewport.append(thumb);
      }
      virtualMedia.set(mediaId, { frame, thumb, onThumbClick, preserveOnVariantChange });
      return true;
    };
    const updateThumbVisibility = (variant) => {
      if (galleryMode !== 'product_media' || !variantMediaIds.size) return;
      const currentVariantMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : '';
      gallery.querySelectorAll('[data-thumb-id]').forEach((thumb) => {
        const id = String(thumb.dataset.thumbId || '');
        thumb.hidden = variantMediaIds.has(id) && currentVariantMediaId && id !== currentVariantMediaId;
      });
      nativeFrames().forEach((frame) => {
        const id = String(frame.dataset.mediaId || '');
        frame.hidden = Boolean(variantMediaIds.has(id) && currentVariantMediaId && id !== currentVariantMediaId);
      });
    };
    const move = (direction, { nativeOnly = false, source = 'navigation', wrap = true, fromMediaId = '' } = {}) => {
      const thumbs = visibleThumbs({ nativeOnly });
      if (!thumbs.length) return;
      const requestedId = String(fromMediaId || activeMediaId);
      const currentId = nativeOnly && !nativeMediaIds.has(requestedId) ? lastNativeMediaId : requestedId;
      const currentIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.dataset.thumbId === currentId));
      const requestedIndex = currentIndex + direction;
      const nextIndex = wrap
        ? (requestedIndex + thumbs.length) % thumbs.length
        : Math.max(0, Math.min(thumbs.length - 1, requestedIndex));
      if (nextIndex === currentIndex) return;
      setMedia(thumbs[nextIndex].dataset.thumbId, true, source);
    };
    const scrollThumbs = (direction) => {
      if (!thumbsViewport) return;
      const amount = Math.max(120, Math.round(thumbsViewport.clientWidth * 0.72));
      thumbsViewport.scrollBy({ left: amount * direction, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    if (stage) {
      const trackFrames = () => Array.from(gallery.querySelectorAll('[data-media-id]'))
        .filter((frame) => !frame.hidden);
      const nearestTrackFrame = () => trackFrames().reduce((nearest, frame) => {
        if (!nearest) return frame;
        const stageRect = stage.getBoundingClientRect();
        const rtl = getComputedStyle(stage).direction === 'rtl';
        const frameDistance = Math.abs(rtl
          ? frame.getBoundingClientRect().right - stageRect.right
          : frame.getBoundingClientRect().left - stageRect.left);
        const nearestDistance = Math.abs(rtl
          ? nearest.getBoundingClientRect().right - stageRect.right
          : nearest.getBoundingClientRect().left - stageRect.left);
        return frameDistance < nearestDistance ? frame : nearest;
      }, null);
      const commitTrackPosition = () => {
        if (!mobileTrack.matches) return;
        const frame = nearestTrackFrame();
        if (frame) setMedia(frame.dataset.mediaId, true, trackScrollSource, false);
        trackScrollSource = 'swipe';
      };
      const queueTrackPosition = () => {
        if (!mobileTrack.matches) return;
        cancelAnimationFrame(trackScrollFrame);
        trackScrollFrame = requestAnimationFrame(() => {
          const frames = trackFrames();
          const frame = nearestTrackFrame();
          if (frame) {
            hydrateNeighbors(frames, frames.indexOf(frame));
            setMedia(frame.dataset.mediaId, false, trackScrollSource, false);
          }
        });
        window.clearTimeout(trackScrollTimer);
        trackScrollTimer = window.setTimeout(commitTrackPosition, 140);
      };
      listen(stage, 'scroll', queueTrackPosition, { passive: true });
      listen(stage, 'scrollend', commitTrackPosition);
      listen(stage, 'pointerdown', () => { galleryInteracted = true; }, { passive: true });
      listen(mobileTrack, 'change', () => {
        if (mobileTrack.matches) requestAnimationFrame(() => scrollStageToFrame(
          trackFrames().find((frame) => frame.dataset.mediaId === activeMediaId),
          'resize'
        ));
      });
      listen(window, 'resize', () => {
        if (!mobileTrack.matches) return;
        cancelAnimationFrame(trackScrollFrame);
        trackScrollFrame = requestAnimationFrame(() => scrollStageToFrame(
          trackFrames().find((frame) => frame.dataset.mediaId === activeMediaId),
          'resize'
        ));
      });
      listen(stage, 'dragstart', (event) => event.preventDefault());
    }
    gallery.querySelectorAll('[data-thumb-id]').forEach((button) => {
      listen(button, 'click', () => { galleryInteracted = true; setMedia(button.dataset.thumbId, true, 'thumb'); });
    });
    listen(previousButton, 'click', () => { galleryInteracted = true; move(-1); });
    listen(nextButton, 'click', () => { galleryInteracted = true; move(1); });
    listen(thumbPreviousButton, 'click', () => scrollThumbs(-1));
    listen(thumbNextButton, 'click', () => scrollThumbs(1));
    listen(root, 'swv5:variant-change', (event) => {
      const variant = event.detail?.variant;
      updateThumbVisibility(variant);
      if (!event.detail?.colorChanged) return;
      const colorMediaId = event.detail?.colorMediaId;
      const variantMediaId = variant?.featured_media?.id || '';
      if (virtualMedia.get(activeMediaId)?.preserveOnVariantChange) {
        const nextNativeId = [colorMediaId, variantMediaId]
          .map((id) => String(id || ''))
          .find((id) => nativeMediaIds.has(id));
        if (nextNativeId) lastNativeMediaId = nextNativeId;
        return;
      }
      if (!colorMediaId || !setMedia(colorMediaId, true, 'variant')) setMedia(variantMediaId, true, 'variant');
    });
    root.querySelectorAll('[data-color-media-id]').forEach((input) => {
      listen(input, 'change', () => {
        galleryInteracted = true;
        if (!root.swv5Purchase && input.checked) setMedia(input.dataset.colorMediaId, true);
      });
    });
    const currentVariant = variants.find((variant) => Number(variant.id) === Number(root.querySelector('[data-variant-id]')?.value)) || null;
    updateThumbVisibility(currentVariant);
    setMedia(activeMediaId, false, 'initial');
    const realignInitialMedia = () => {
      if (!mobileTrack.matches || galleryInteracted || !initialMediaId) return;
      window.cancelAnimationFrame(initialAlignmentFrame);
      initialAlignmentFrame = window.requestAnimationFrame(() => {
        initialAlignmentFrame = window.requestAnimationFrame(() => { if (!galleryInteracted) setMedia(initialMediaId, false, 'initial'); });
      });
    };
    realignInitialMedia();
    initialAlignmentTimer = window.setTimeout(realignInitialMedia, 350);
    listen(window, 'pageshow', realignInitialMedia);
    if (document.readyState !== 'complete') listen(window, 'load', realignInitialMedia, { once: true });
    const api = {
      registerVirtualMedia,
      unregisterVirtualMedia,
      setMedia,
      restoreNative,
      isActive: (mediaId) => activeMediaId === String(mediaId || ''),
      get activeMediaId() {
        return activeMediaId;
      }
    };
    root.swv5Gallery = api;
    root.dispatchEvent(new CustomEvent('swv5:gallery-ready', { bubbles: true, detail: { api } }));

    return () => {
      window.clearTimeout(trackScrollTimer);
      window.clearTimeout(initialAlignmentTimer);
      cancelAnimationFrame(trackScrollFrame);
      cancelAnimationFrame(initialAlignmentFrame);
      cancelDeferredNeighborHydration();
      Array.from(virtualMedia.keys()).forEach(unregisterVirtualMedia);
      if (root.swv5Gallery === api) delete root.swv5Gallery;
      gallery.querySelectorAll('video').forEach((video) => video.pause());
    };
  });
})();
